import { MsalContext } from "@azure/msal-react";
import * as Popover from "@radix-ui/react-popover";
import React, { useState, useContext } from "react";
import { connect } from "react-redux";
import { userActions } from "../../actions";
import "../../../scss/components/_header-modernized.scss";

interface HeaderUserDropdownModernizedProps {
  user?: {
    id?: number;
    name?: string;
    email?: string;
    companyId?: number;
  };
  designVariant?: "minimal" | "bold" | "gradient" | "split";
  dispatch: (action: any) => void;
  toggleHelpVisible?: () => void;
  setHelpDataAll?: (value: boolean) => void;
}

const HeaderUserDropdownModernized: React.FC<
  HeaderUserDropdownModernizedProps
> = ({ user, designVariant = "bold", dispatch, setHelpDataAll }) => {
  const [open, setOpen] = useState(false);
  const msalContext = useContext(MsalContext);
  const instance = msalContext?.instance;

  const handleCopyUserId = () => {
    if (user?.id) {
      navigator.clipboard.writeText(user.id.toString());
      setOpen(false);
    }
  };

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(userActions.logout());
    setOpen(false);
  };

  const handleManageAccount = () => {
    window.location.href = "#/manageAccount";
    setOpen(false);
  };

  const handlePortalHelp = () => {
    if (setHelpDataAll) {
      setHelpDataAll(true);
    }
    setOpen(false);
  };

  const handleCookieSettings = () => {
    if ((window as any).reopenCookieBanner) {
      (window as any).reopenCookieBanner();
    } else {
      document.cookie =
        "CookieConsent=; Max-Age=0; path=/; SameSite=Lax";
      window.location.reload();
    }
    setOpen(false);
  };

  const logoutMessage = user ? "Logout" : "Return to login page";

  return (
    <div className={`header-user-dropdown header-user-dropdown-${designVariant}`}>
      <div className="header-welcome-text">
        Welcome {user?.name || ""}
      </div>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            className="header-user-button"
            aria-label="User menu"
            type="button"
          >
            <i className="fas fa-user" aria-hidden="true" />
            <i className="fas fa-angle-down" aria-hidden="true" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="header-user-popover-content"
            sideOffset={5}
            align="end"
            onInteractOutside={(e) => {
              // Prevent closing when interacting with elements inside the popover
              const target = e.target as HTMLElement;
              if (
                target.closest('.header-user-popover-content') ||
                target.closest('.header-user-popover-footer') ||
                target.closest('.header-logout-button')
              ) {
                e.preventDefault();
              }
            }}
            onEscapeKeyDown={(e) => {
              // Allow ESC to close
            }}
          >
            <div className="header-user-popover-header">
              <div className="header-user-avatar-container">
                <img
                  src="img/avatars/userImg.png"
                  className="header-user-avatar"
                  alt="User avatar"
                />
              </div>
              {user && (
                <div className="header-user-info">
                  <div className="header-user-name-row">
                    <i className="icon-user" aria-hidden="true" />
                    <span>{user.name}</span>
                    <button
                      onClick={handleCopyUserId}
                      className="header-copy-button"
                      aria-label="Copy User ID"
                      title="Copy User ID"
                    >
                      <i className="fas fa-copy" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="header-user-email-row">
                    <i className="icon-envelope" aria-hidden="true" />
                    <span>{user.email}</span>
                  </div>
                  <div className="header-user-actions">
                    <button
                      onClick={handleManageAccount}
                      className="header-action-button"
                    >
                      <i className="fas fa-user-cog" aria-hidden="true" />
                      <span>Manage Account</span>
                    </button>
                    {user.companyId === 2 && (
                      <button
                        onClick={handlePortalHelp}
                        className="header-action-button"
                      >
                        <i className="fas fa-question-circle" aria-hidden="true" />
                        <span>Portal Help</span>
                      </button>
                    )}
                    <button
                      onClick={handleCookieSettings}
                      className="header-action-button"
                    >
                      <i className="fas fa-cookie-bite" aria-hidden="true" />
                      <span>Cookie Settings</span>
                    </button>
                  </div>
                  <div className="header-user-version" data-clarity-unmask="true">
                    Version:{" "}
                    {window.process?.env?.REACT_APP_RELEASE ?? "local version"}
                  </div>
                </div>
              )}
            </div>
            <div className="header-user-popover-footer">
              <button
                onClick={handleLogout}
                className="header-logout-button"
              >
                <i className="icon-logout" aria-hidden="true" />
                <span>{logoutMessage}</span>
              </button>
            </div>
            <Popover.Arrow className="header-user-popover-arrow" />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};

function mapStateToProps(state: any) {
  const { authentication } = state;
  const { user } = authentication;
  return {
    user,
  };
}

const connectedHeaderUserDropdown = connect(mapStateToProps)(
  HeaderUserDropdownModernized,
);
export default connectedHeaderUserDropdown;
